import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  UseTopPlayers,
  UseLeaderboardGroupKpis,
  UseGroupKpis,
  UseGroupAllKpis,
  useGetPreviousKpis,
} from "../../../hooks/kpi";
import { UseSelectedWorkspace } from "../../../hooks/company";
import { lastMonthMoment } from "../../../helpers/historicMonthBoard";
import useLeaderboardData from "../LeaderboardTable/data";

import LiveLeaderboardComponent from "./LiveLeaderboard";

const IDX_TO_BUCKET = { 0: "yesterday", 1: "today", 2: "week", 4: "lastWeek", 3: "month", 5: "lastMonth" };

/**
 * Every navbar date window uses ONE scoring path (useLeaderboardData) for both
 * players and Yeti — same live workspace defs + clipped [from,to] as KOTM.
 * Mixing UseTopPlayers for players with a different Yeti source is what made
 * This Week show Yeti 300 / Bobby 40 on the board while the mountain sat at
 * 300 / 227.
 */
const BOARD_WINDOWS = new Set([
  "today", "yesterday", "week", "lastWeek", "month", "lastMonth",
  "custom", "quarter", "ytd", "year",
]);

const yetiRowOf = (yetiPts, yetiScores = null) => {
  const yPts = Math.round(Number(yetiPts) || 0);
  const scores = yetiScores && typeof yetiScores === "object"
    ? { ...yetiScores, total: yPts }
    : { total: yPts };
  return {
    userId: "__yeti__",
    firstName: "The",
    lastName: "Yeti",
    image: "",
    email: "",
    isActive: true,
    isYeti: true,
    ownId: false,
    scores,
  };
};

const rowsToTopList = (rows, yetiPts, meUserId, yetiScores = null) => {
  const players = (rows || []).map((row) => {
    const first = row.firstName || (row.name || "").split(" ")[0] || "";
    const last = row.lastName
      || (row.name || "").split(" ").slice(1).join(" ")
      || "";
    const pts = Math.round(Number(row.pts) || 0);
    const scores = row.scores && typeof row.scores === "object"
      ? { ...row.scores, total: Number(row.scores.total) || pts }
      : { total: pts };
    return {
      userId: row.userId,
      firstName: first,
      lastName: last,
      image: row.image || "",
      email: row.email || "",
      isActive: row.isActive !== false,
      isYeti: false,
      ownId: meUserId && String(row.userId) === String(meUserId),
      scores,
    };
  });

  players.push(yetiRowOf(yetiPts, yetiScores));
  return players.sort((a, b) => (b.scores?.total || 0) - (a.scores?.total || 0));
};

const LiveLeaderboard = ({
  periodIdx,
  kpiId,
  isAdmin = false,
  section = "full",
  boardWindow = null,
  boardCustom = null,
  workspaceId = null,
}) => {
  const groupKpis = UseLeaderboardGroupKpis();
  const allGroupKpis = UseGroupKpis();
  const groupAllKpis = UseGroupAllKpis();
  const { getPreviousKpis } = useGetPreviousKpis();
  const selected = UseSelectedWorkspace();
  const meUserId = useSelector((s) => s.authReducer.userDetails?._id);
  const isLastMonth = periodIdx === 5;
  const catalogReady = !!(groupAllKpis?.length || allGroupKpis?.length);

  const lastMonthRef = useMemo(() => lastMonthMoment(), []);
  const bucket = IDX_TO_BUCKET[periodIdx] || "month";

  /* Fallback only when there is no navbar window (embedded / legacy). */
  const topPlayersByBucket = {
    yesterday: UseTopPlayers("yesterday"),
    today: UseTopPlayers("today"),
    week: UseTopPlayers("week"),
    lastWeek: UseTopPlayers("lastWeek"),
    lastMonth: UseTopPlayers("month", lastMonthRef),
    month: UseTopPlayers("month"),
  };
  const currentPlayers = topPlayersByBucket[bucket];

  const wsId = workspaceId || selected?._id || null;
  let dataBucket = boardWindow || bucket;
  if (dataBucket === "year") dataBucket = "ytd";

  const useBoardData = !!wsId
    && BOARD_WINDOWS.has(dataBucket)
    && !(dataBucket === "custom" && !(boardCustom?.from && boardCustom?.to));

  const dataCustom = dataBucket === "custom" ? boardCustom : null;
  const dataIds = useMemo(
    () => (useBoardData && wsId ? [String(wsId)] : []),
    [useBoardData, wsId],
  );

  const {
    rows: boardRows,
    yetiPts: boardYetiPts,
    yetiScores: boardYetiScores,
  } = useLeaderboardData(
    dataIds,
    dataBucket || "month",
    null,
    dataCustom,
  );

  useEffect(() => {
    if (isLastMonth && catalogReady) getPreviousKpis(lastMonthRef, true);
  }, [isLastMonth, lastMonthRef, getPreviousKpis, catalogReady]);

  const boardList = useMemo(
    () => (useBoardData
      ? rowsToTopList(boardRows, boardYetiPts, meUserId, boardYetiScores)
      : null),
    [useBoardData, boardRows, boardYetiPts, boardYetiScores, meUserId],
  );

  const topList = useMemo(() => {
    if (boardList) return boardList;
    if (!currentPlayers) return [];
    if (kpiId === "default") return currentPlayers;
    return currentPlayers
      .map((player) => ({
        ...player,
        scores: {
          ...player.scores,
          total: player.scores?.[kpiId]?.pts || 0,
        },
      }))
      .filter((player) => player.scores?.[kpiId] !== undefined);
  }, [boardList, currentPlayers, kpiId]);

  return (
    <LiveLeaderboardComponent
      topList={topList}
      groupKpis={groupKpis}
      allGroupKpis={allGroupKpis}
      isAdmin={isAdmin}
      periodIdx={periodIdx}
      kpiId={useBoardData ? "default" : kpiId}
      section={section}
      usePrecomputedTotal={useBoardData}
    />
  );
};

export default LiveLeaderboard;
